/* Libraries */
import React, { useRef } from 'react';

/* Components */
import Either from '@components/Either';

/* State: Client App */
import { useAppStoreActions } from '@store/client/store.app';
import { MODAL_LOGIN_PASSWORDLESS, useModalStoreActions } from '@store/client/store.modals';

/* Mutation */
import useUserMutationLogout from '@store/queries/user/useUserMutation.logout';

/* Hooks */
import { useIsPasswordlessCheckoutEnabled } from '@scenes/LoginPage/Modal/LoginPasscode/LoginPasscode.hooks';

/* Utils && Constants */
import { prevented, noop } from '@utils/event';
import useOutsideClick from '@hooks/useOutsideClick';
import { LOGGED_IN_MENU, LOGGED_OUT_MENU, PROFILE_MENU_ACTIONS } from '../constants';

/* Styles */
import styles from '../standardheader.module.scss';

/* Types */
type LoggedInMenuProps = {
  toggleAccountMenu: BaseAnyFunction;
};

type LoggedOutMenuProps = {
  openLoginModal: BaseAnyFunction;
  toggleAccountMenu: BaseAnyFunction;
};

type AccountMenuItemsProps = {
  isSignedIn: boolean;
  openLoginModal: BaseAnyFunction;
  toggleAccountMenu: BaseAnyFunction;
};

const LoggedInMenu = ({ toggleAccountMenu }: LoggedInMenuProps) => {
  const removeLogoutMutation = useUserMutationLogout();
  const logoutUser = () => removeLogoutMutation.mutate();

  return (
    <>
      {LOGGED_IN_MENU.map((item, index) => {
        const { link } = item;

        return (
          <div key={`link${index}`} className={styles.accountMenuItem} onClick={toggleAccountMenu}>
            <Either condition={Boolean(link)}>
              <a href={link}>
                <div>{item.name}</div>
              </a>
              <div onClick={logoutUser}>
                <a>{item.name}</a>
              </div>
            </Either>
          </div>
        );
      })}
    </>
  );
};

const LoggedOutMenu = ({ openLoginModal, toggleAccountMenu }: LoggedOutMenuProps) => {
  const isPasswordlessCheckoutEnabled = useIsPasswordlessCheckoutEnabled();
  const { updateAppLevelModal } = useModalStoreActions();
  const handleActionClick = (action?: string) => {
    toggleAccountMenu();
    if (action === PROFILE_MENU_ACTIONS.SIGN_IN) {
      if (isPasswordlessCheckoutEnabled) {
        updateAppLevelModal({ name: MODAL_LOGIN_PASSWORDLESS, visible: true });
      } else {
        prevented(openLoginModal(true));
      }
    }
  };

  return (
    <>
      {LOGGED_OUT_MENU.map((item, index) => {
        const { action, link } = item;

        if (action) {
          return (
            <div key={index} className={styles.accountMenuItem} onClick={() => handleActionClick(item.action)}>
              <div>{item.name}</div>
            </div>
          );
        }
        if (link) {
          return (
            <div key={item.name} className={styles.accountMenuItem} onClick={toggleAccountMenu}>
              <a href={link}>
                <div>{item.name}</div>
              </a>
            </div>
          );
        }
        return null;
      })}
    </>
  );
};

const AccountMenuItems = ({ isSignedIn = false, toggleAccountMenu = noop }: AccountMenuItemsProps) => {
  const wrapperRef = useRef(null);
  const { setOpenSlideModalLogin } = useAppStoreActions();

  useOutsideClick(wrapperRef, toggleAccountMenu);

  return (
    <div ref={wrapperRef} data-component="AccountMenuItems" className={styles.accountMenu} data-testid="nav-account-menu">
      <Either condition={Boolean(isSignedIn)}>
        <LoggedInMenu toggleAccountMenu={toggleAccountMenu} />
        <LoggedOutMenu
          toggleAccountMenu={toggleAccountMenu}
          openLoginModal={(open: boolean) => {
            // Router.push(loginPath());
            setOpenSlideModalLogin(open);
          }}
        />
      </Either>
    </div>
  );
};

export default React.memo(AccountMenuItems);
